import React, { useState } from "react";
import { Accordion, Form, Button, Row, Col, Badge, Alert } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { server } from "../../util/server";
import axiosInstance from "../../source/axiosInstance";
import "./PropertyForm.css";
import "./PropertyFormAccordion.css";

function PropertyFormImproved({ property = null, setEditProperty = () => {} }) {
  const navigate = useNavigate();
  
  // Estados para campos obrigatórios
  const [title, setTitle] = useState(property?.title || "");
  const [code, setCode] = useState(property?.code || "");
  const [category, setCategory] = useState(property?.category || "");
  const [location, setLocation] = useState(property?.location || "");
  const [price, setPrice] = useState(property?.price || "");
  const [description, setDescription] = useState(property?.description || "");
  
  // Estados para campos comuns
  const [fullDescription, setFullDescription] = useState(property?.full_description || "");
  const [details, setDetails] = useState(property?.details?.join(",") || "");
  const [state, setState] = useState(property?.estadoId || "");
  const [city, setCity] = useState(property?.cidadeId || "");
  const [ac, setAc] = useState(property?.ac || "");
  const [at, setAt] = useState(property?.at || "");
  
  // Quartos e Banheiros
  const [dorm, setDorm] = useState(property?.dorm || 0);
  const [suite, setSuite] = useState(property?.suite || 0);
  const [banheiros, setBanheiros] = useState(property?.banheiros || 0);
  const [vagas, setVagas] = useState(property?.vagas || "");
  
  // Comodidades (checkboxes)
  const [piscina, setPiscina] = useState(property?.piscina || false);
  const [churrasqueira, setChurrasqueira] = useState(property?.churrasqueira || false);
  const [salaoFestas, setSalaoFestas] = useState(property?.salao_festas || false);
  const [quadraEsportes, setQuadraEsportes] = useState(property?.quadra_esportes || false);
  const [lago, setLago] = useState(property?.lago || false);
  const [pomar, setPomar] = useState(property?.pomar || false);
  const [horta, setHorta] = useState(property?.horta || false);
  const [nascente, setNascente] = useState(property?.nascente || false);
  const [casaCaseiro, setCasaCaseiro] = useState(property?.casa_caseiro || false);
  const [casaHospedes, setCasaHospedes] = useState(property?.casa_hospedes || false);
  
  // Validação
  const [validated, setValidated] = useState(false);
  const [loading, setLoading] = useState(false);

  const validateRequired = () => {
    if (!title || !code || !category || !location || !price) {
      toast.error("Preencha todos os campos obrigatórios (*)");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setValidated(true);

    if (!validateRequired()) {
      return;
    }

    setLoading(true);
    
    const formData = {
      title,
      code,
      category,
      location,
      price,
      description,
      full_description: fullDescription,
      details: details.split(",").map(d => d.trim()).filter(d => d),
      estadoId: state,
      cidadeId: city,
      ac,
      at,
      dorm: parseInt(dorm) || 0,
      suite: parseInt(suite) || 0,
      banheiros: parseInt(banheiros) || 0,
      vagas,
      piscina,
      churrasqueira,
      salao_festas: salaoFestas,
      quadra_esportes: quadraEsportes,
      lago,
      pomar,
      horta,
      nascente,
      casa_caseiro: casaCaseiro,
      casa_hospedes: casaHospedes,
    };

    try {
      if (property) {
        // Edição - preservar campos importantes que não estão no formulário
        const updateData = {
          ...formData,
          status: property.status || "I",
          highlight: property.highlight || false,
          highlightImovelWeb: property.highlightImovelWeb || "I",
          image_urls: property.image_urls || [],
          destaque: property.destaque || "SIMPLE",
          bairroId: property.bairroId || "",
        };
        
        await axiosInstance.put(`${server}/api/imoveis/${property._id}`, updateData);
        toast.success("Imóvel atualizado com sucesso!");
      } else {
        // Criação
        await axiosInstance.post(`${server}/api/imoveis`, formData);
        toast.success("Imóvel criado com sucesso!");
      }
      navigate("/");
    } catch (error) {
      console.error("Erro ao salvar imóvel:", error);
      if (error.response && error.response.data && error.response.data.error) {
        toast.error(`Erro: ${error.response.data.error}`);
      } else {
        toast.error("Erro ao salvar imóvel. Tente novamente.");
      }
    } finally {
      setLoading(false);
    }
  };

  const categories = [
    "Áreas industriais a Venda",
    "Chácaras a Venda",
    "Condomínios a Venda",
    "Fazendas a Venda",
    "Haras a Venda",
    "Represa a Venda",
    "Sítios a Venda",
    "Terrenos a Venda",
  ];

  return (
    <div className="property-form-container">
      {/* Header */}
      <div className="form-header">
        <h2>{property ? "Editar Imóvel" : "Criar Novo Imóvel"}</h2>
        <Button variant="light" onClick={() => setEditProperty(null)}>
          Voltar
        </Button>
      </div>

      {/* Alert de campos obrigatórios */}
      <Alert variant="info" className="mb-4">
        <strong>ℹ️ Campos obrigatórios:</strong> Apenas os campos marcados com <Badge bg="danger">*</Badge> são obrigatórios para criar o imóvel.
      </Alert>

      <Form noValidate validated={validated} onSubmit={handleSubmit}>
        
        {/* SEÇÃO 1: INFORMAÇÕES BÁSICAS (OBRIGATÓRIAS) */}
        <Accordion defaultActiveKey="0" className="mb-3">
          <Accordion.Item eventKey="0">
            <Accordion.Header>
              <strong>📋 Informações Básicas</strong>
              <Badge bg="danger" className="ms-2">Obrigatório</Badge>
            </Accordion.Header>
            <Accordion.Body>
              {/* Linha 1: Título (grande) */}
              <Row>
                <Col md={12}>
                  <Form.Group className="mb-4">
                    <Form.Label className="required" style={{ fontSize: '15px', fontWeight: '600' }}>
                      Título do Imóvel
                    </Form.Label>
                    <Form.Control
                      type="text"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      required
                      placeholder="Ex: Fazenda com 50 alqueires em Itu - São Paulo"
                      style={{ fontSize: '18px', padding: '16px 20px', height: '60px', width: '600px' }}
                    />
                  </Form.Group>
                </Col>
              </Row>

              {/* Linha 2: Código e Categoria */}
              <Row>
                <Col md={4}>
                  <Form.Group className="mb-4">
                    <Form.Label className="required" style={{ fontSize: '15px', fontWeight: '600' }}>
                      Código
                    </Form.Label>
                    <Form.Control
                      type="text"
                      value={code}
                      onChange={(e) => setCode(e.target.value)}
                      required
                      placeholder="Ex: 1234"
                      style={{ fontSize: '16px', padding: '14px 18px', height: '54px' }}
                    />
                  </Form.Group>
                </Col>
                <Col md={8}>
                  <Form.Group className="mb-4">
                    <Form.Label className="required" style={{ fontSize: '15px', fontWeight: '600' }}>
                      Categoria
                    </Form.Label>
                    <Form.Select
                      value={category}
                      onChange={(e) => setCategory(e.target.value)}
                      required
                      style={{ fontSize: '16px', padding: '14px 18px', height: '54px' }}
                    >
                      <option value="">Selecione a categoria...</option>
                      {categories.map((cat) => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              {/* Linha 3: Localização (grande) */}
              <Row>
                <Col md={12}>
                  <Form.Group className="mb-4">
                    <Form.Label className="required" style={{ fontSize: '15px', fontWeight: '600' }}>
                      Localização
                    </Form.Label>
                    <Form.Control
                      type="text"
                      value={location}
                      onChange={(e) => setLocation(e.target.value)}
                      required
                      placeholder="Ex: Itu, SP - Bairro Centro"
                      style={{ fontSize: '18px', padding: '16px 20px', height: '60px' }}
                    />
                  </Form.Group>
                </Col>
              </Row>

              {/* Linha 4: Preço (grande) */}
              <Row>
                <Col md={12}>
                  <Form.Group className="mb-4">
                    <Form.Label className="required" style={{ fontSize: '15px', fontWeight: '600' }}>
                      Preço
                    </Form.Label>
                    <Form.Control
                      type="text"
                      value={price}
                      onChange={(e) => setPrice(e.target.value)}
                      required
                      placeholder="Ex: R$ 2.500.000,00"
                      style={{ fontSize: '18px', padding: '16px 20px', height: '60px' }}
                    />
                  </Form.Group>
                </Col>
              </Row>

              {/* Linha 5: Descrição Curta */}
              <Form.Group className="mb-3">
                <Form.Label style={{ fontSize: '15px', fontWeight: '600' }}>
                  Descrição Curta (opcional)
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Descrição breve do imóvel (aparece no card)"
                  style={{ fontSize: '16px', padding: '14px 18px', lineHeight: '1.6' }}
                />
                <Form.Text className="text-muted">
                  Este texto aparece no cartão de visualização rápida do imóvel
                </Form.Text>
              </Form.Group>
            </Accordion.Body>
          </Accordion.Item>

          {/* SEÇÃO 2: DETALHES DO IMÓVEL */}
          <Accordion.Item eventKey="1">
            <Accordion.Header>
              <strong>🏡 Detalhes do Imóvel</strong>
              <Badge bg="secondary" className="ms-2">Opcional</Badge>
            </Accordion.Header>
            <Accordion.Body>
              <Row>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>Área Total (m²)</Form.Label>
                    <Form.Control
                      type="text"
                      value={at}
                      onChange={(e) => setAt(e.target.value)}
                      placeholder="Ex: 10000"
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>Área Construída (m²)</Form.Label>
                    <Form.Control
                      type="text"
                      value={ac}
                      onChange={(e) => setAc(e.target.value)}
                      placeholder="Ex: 500"
                    />
                  </Form.Group>
                </Col>
                <Col md={2}>
                  <Form.Group className="mb-3">
                    <Form.Label>Dormitórios</Form.Label>
                    <Form.Control
                      type="number"
                      value={dorm}
                      onChange={(e) => setDorm(e.target.value)}
                      min="0"
                    />
                  </Form.Group>
                </Col>
                <Col md={2}>
                  <Form.Group className="mb-3">
                    <Form.Label>Suítes</Form.Label>
                    <Form.Control
                      type="number"
                      value={suite}
                      onChange={(e) => setSuite(e.target.value)}
                      min="0"
                    />
                  </Form.Group>
                </Col>
                <Col md={2}>
                  <Form.Group className="mb-3">
                    <Form.Label>Banheiros</Form.Label>
                    <Form.Control
                      type="number"
                      value={banheiros}
                      onChange={(e) => setBanheiros(e.target.value)}
                      min="0"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Vagas de Garagem</Form.Label>
                    <Form.Control
                      type="text"
                      value={vagas}
                      onChange={(e) => setVagas(e.target.value)}
                      placeholder="Ex: 4 vagas cobertas"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mb-3">
                <Form.Label>Descrição Completa</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={5}
                  value={fullDescription}
                  onChange={(e) => setFullDescription(e.target.value)}
                  placeholder="Descrição detalhada do imóvel (aparece na página de detalhes)"
                />
                <Form.Text className="text-muted">
                  Este texto aparecerá na página individual do imóvel
                </Form.Text>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Detalhes (separados por vírgula)</Form.Label>
                <Form.Control
                  type="text"
                  value={details}
                  onChange={(e) => setDetails(e.target.value)}
                  placeholder="Ex: Piscina, Churrasqueira, Quadra, Lago"
                />
                <Form.Text className="text-muted">
                  Separe cada detalhe com vírgula. Aparecerá como lista no site.
                </Form.Text>
              </Form.Group>
            </Accordion.Body>
          </Accordion.Item>

          {/* SEÇÃO 3: COMODIDADES */}
          <Accordion.Item eventKey="2">
            <Accordion.Header>
              <strong>✨ Comodidades</strong>
              <Badge bg="secondary" className="ms-2">Opcional</Badge>
            </Accordion.Header>
            <Accordion.Body>
              <p className="text-muted mb-3">
                Marque as comodidades que o imóvel possui. Estas informações aparecem na busca e nos filtros do site.
              </p>
              <Row>
                <Col md={3}>
                  <Form.Check
                    type="checkbox"
                    label="🏊 Piscina"
                    checked={piscina}
                    onChange={(e) => setPiscina(e.target.checked)}
                    className="mb-3"
                  />
                  <Form.Check
                    type="checkbox"
                    label="🍖 Churrasqueira"
                    checked={churrasqueira}
                    onChange={(e) => setChurrasqueira(e.target.checked)}
                    className="mb-3"
                  />
                  <Form.Check
                    type="checkbox"
                    label="🎉 Salão de Festas"
                    checked={salaoFestas}
                    onChange={(e) => setSalaoFestas(e.target.checked)}
                    className="mb-3"
                  />
                </Col>
                <Col md={3}>
                  <Form.Check
                    type="checkbox"
                    label="🏀 Quadra de Esportes"
                    checked={quadraEsportes}
                    onChange={(e) => setQuadraEsportes(e.target.checked)}
                    className="mb-3"
                  />
                  <Form.Check
                    type="checkbox"
                    label="🌊 Lago"
                    checked={lago}
                    onChange={(e) => setLago(e.target.checked)}
                    className="mb-3"
                  />
                  <Form.Check
                    type="checkbox"
                    label="💧 Nascente"
                    checked={nascente}
                    onChange={(e) => setNascente(e.target.checked)}
                    className="mb-3"
                  />
                </Col>
                <Col md={3}>
                  <Form.Check
                    type="checkbox"
                    label="🍎 Pomar"
                    checked={pomar}
                    onChange={(e) => setPomar(e.target.checked)}
                    className="mb-3"
                  />
                  <Form.Check
                    type="checkbox"
                    label="🥬 Horta"
                    checked={horta}
                    onChange={(e) => setHorta(e.target.checked)}
                    className="mb-3"
                  />
                  <Form.Check
                    type="checkbox"
                    label="🏠 Casa de Caseiro"
                    checked={casaCaseiro}
                    onChange={(e) => setCasaCaseiro(e.target.checked)}
                    className="mb-3"
                  />
                </Col>
                <Col md={3}>
                  <Form.Check
                    type="checkbox"
                    label="🏡 Casa de Hóspedes"
                    checked={casaHospedes}
                    onChange={(e) => setCasaHospedes(e.target.checked)}
                    className="mb-3"
                  />
                </Col>
              </Row>
            </Accordion.Body>
          </Accordion.Item>

          {/* SEÇÃO 4: LOCALIZAÇÃO */}
          <Accordion.Item eventKey="3">
            <Accordion.Header>
              <strong>📍 Localização Detalhada</strong>
              <Badge bg="secondary" className="ms-2">Opcional</Badge>
            </Accordion.Header>
            <Accordion.Body>
              <p className="text-muted mb-3">
                Informações adicionais de localização (usado para integração com ImovelWeb)
              </p>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Estado (ID ImovelWeb)</Form.Label>
                    <Form.Control
                      type="text"
                      value={state}
                      onChange={(e) => setState(e.target.value)}
                      placeholder="Ex: V1-B-265"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Cidade (ID ImovelWeb)</Form.Label>
                    <Form.Control
                      type="text"
                      value={city}
                      onChange={(e) => setCity(e.target.value)}
                      placeholder="Ex: V1-N-1234"
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Accordion.Body>
          </Accordion.Item>
        </Accordion>

        {/* Botões de Ação */}
        <div className="form-actions">
          <Button 
            variant="secondary" 
            onClick={() => setEditProperty(null)}
            disabled={loading}
          >
            Cancelar
          </Button>
          <Button 
            variant="primary" 
            type="submit"
            disabled={loading}
          >
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" />
                Salvando...
              </>
            ) : (
              property ? "Atualizar Imóvel" : "Criar Imóvel"
            )}
          </Button>
        </div>
      </Form>
    </div>
  );
}

export default PropertyFormImproved;

